import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CaretDown, CaretRight, DownloadSimple, Scroll, ArrowUp, ArrowDown, Crown } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const Delta = ({ now, prev, testid }) => {
  if (prev == null || now === prev) return null;
  const up = now > prev;
  return (
    <span className="inline-flex items-center gap-0.5 font-bold" data-testid={testid}
      style={{ color: up ? "#166534" : "#991B1B" }}
      title={`${up ? "Up" : "Down"} ${Math.abs(now - prev)} vs the month before`}>
      {up ? <ArrowUp size={11} weight="bold" /> : <ArrowDown size={11} weight="bold" />}{Math.abs(now - prev)}
    </span>
  );
};

export const RecapArchiveCard = () => {
  const [open, setOpen] = useState(false);
  const [recaps, setRecaps] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    if (open && recaps === null) {
      api.get("/gamification/recaps").then(({ data }) => setRecaps(data)).catch(() => setRecaps([]));
    }
  }, [open, recaps]);

  const download = async (r) => {
    setDownloading(r.id);
    try {
      const res = await api.get(`/gamification/recaps/${r.id}/image`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `self-agency-${r.month_key}-recap.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not create that recap image"); }
    finally { setDownloading(""); }
  };

  return (
    <div className="card mb-3" data-testid="recap-archive-card">
      <button className="w-full flex items-center gap-2 bg-transparent border-0 cursor-pointer p-0 text-left"
        onClick={() => setOpen((o) => !o)} data-testid="recap-archive-toggle">
        {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        <Scroll size={18} weight="duotone" color="#0A6B7A" />
        <span className="font-bold font-display text-sm">My monthly recaps</span>
      </button>
      {open && (
        <div className="mt-3">
          {recaps === null && <div className="text-xs text-[#000000]">Loading…</div>}
          {recaps?.length === 0 && (
            <div className="text-sm text-[#000000]" data-testid="recap-archive-empty">
              Your first recap lands on the 1st of next month — keep those lessons rolling.
            </div>
          )}
          {recaps?.map((r, i) => {
            const prev = recaps[i + 1] || null;
            const bestId = recaps.length >= 2
              ? [...recaps].sort((a, b) => b.lessons - a.lessons || b.longest - a.longest)[0].id
              : null;
            const isBest = r.id === bestId;
            return (
            <div key={r.id}
              className={`flex items-center gap-3 border rounded-lg p-2.5 mb-2 flex-wrap ${isBest ? "border-[#D99E02] bg-[#FFFBEB]" : "border-[#EDEEF0]"}`}
              data-testid={`recap-row-${r.month_key}`}>
              <div className="font-bold text-sm min-w-[110px] flex items-center gap-1.5">
                {r.month_name}
                {isBest && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full"
                    style={{ background: "#D99E02", color: "#fff" }} title="Your strongest month yet — beat it!"
                    data-testid={`recap-best-badge-${r.month_key}`}>
                    <Crown size={11} weight="fill" /> Best
                  </span>
                )}
              </div>
              <div className="text-xs text-[#000000] flex-1 min-w-0">
                {r.lessons} lesson{r.lessons !== 1 ? "s" : ""} <Delta now={r.lessons} prev={prev?.lessons} testid={`recap-delta-lessons-${r.month_key}`} />
                {" "}· {r.coins_text} {r.coins_label} · {r.longest}-day best streak <Delta now={r.longest} prev={prev?.longest} testid={`recap-delta-streak-${r.month_key}`} />
                {" "}· <span className="font-bold" style={{ color: r.level_color }}>{r.level_title}</span>
              </div>
              <button className="btn-ghost p-1.5 text-[#0A6B7A]" title="Download the shareable graphic"
                disabled={downloading === r.id} onClick={() => download(r)} data-testid={`recap-download-${r.month_key}`}>
                <DownloadSimple size={16} weight="bold" />
              </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
